
import React, { useState, useEffect } from 'react';
import { UserProfile, DecisionAnalysis, PredictiveAlert } from '../types';
import { simulateDecision, getPredictiveAlerts } from '../services/geminiService';
// Added Sparkles to imports
import { TrendingUp, GitMerge, AlertTriangle, ArrowRight, Brain, Zap, Activity, Clock, Loader2, RefreshCw, BarChart3, ShieldCheck, Sparkles } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from 'recharts';
import { TRANSLATIONS } from '../translations';

interface Props {
  user: UserProfile;
}

const PredictionLab: React.FC<Props> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'simulator' | 'alerts'>('simulator');
  const t = TRANSLATIONS[user.language];
  
  const [scenarioInput, setScenarioInput] = useState('');
  const [simulationResult, setSimulationResult] = useState<DecisionAnalysis | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  const [alerts, setAlerts] = useState<PredictiveAlert[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  useEffect(() => {
    handleLoadAlerts();
  }, []);

  const handleLoadAlerts = async () => {
    setLoadingAlerts(true);
    try {
        const results = await getPredictiveAlerts(user);
        setAlerts(results);
    } catch (e) {
        console.error(e);
    } finally {
        setLoadingAlerts(false);
    }
  };

  const handleSimulate = async () => {
    if (!scenarioInput.trim()) return;
    setIsSimulating(true);
    setSimulationResult(null);
    try {
        const result = await simulateDecision(user, scenarioInput);
        setSimulationResult(result);
    } catch (e) {
        console.error(e);
    } finally {
        setIsSimulating(false);
    }
  };

  const getAlertColor = (type: string) => {
      switch(type) {
          case 'health': return 'bg-red-50 text-red-600 border-red-200';
          case 'finance': return 'bg-emerald-50 text-emerald-600 border-emerald-200';
          case 'work': return 'bg-blue-50 text-blue-600 border-blue-200';
          case 'social': return 'bg-pink-50 text-pink-600 border-pink-200';
          default: return 'bg-slate-50 text-slate-600 border-slate-200';
      }
  };

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Zap className="w-6 h-6 text-purple-600" />
             {t.prediction_lab_title}
           </h2>
           <p className="text-slate-500 text-sm mt-1">{t.prediction_lab_desc}</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button 
                onClick={() => setActiveTab('simulator')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'simulator' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>
                {t.decision_simulator}
            </button>
            <button 
                onClick={() => setActiveTab('alerts')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'alerts' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>
                {t.predictive_alerts}
            </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
          {activeTab === 'simulator' && (
              <div className="space-y-6">
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-purple-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
                      <label className="block text-slate-700 font-black mb-4 flex items-center gap-2 text-lg">
                          <GitMerge className="w-6 h-6 text-purple-500" />
                          {t.decision_prompt}
                      </label>
                      <div className="flex flex-col gap-4">
                          <textarea 
                              value={scenarioInput}
                              onChange={(e) => setScenarioInput(e.target.value)}
                              placeholder="بۆ نموونە: ئەگەر کارەکەم بگۆڕم و بچمە ناو بوارێکی نوێ..."
                              className="w-full h-32 bg-slate-50 border border-slate-200 rounded-[2rem] px-6 py-4 focus:outline-none focus:ring-4 focus:ring-purple-100 transition-all text-lg"
                              onKeyDown={(e) => e.ctrlKey && e.key === 'Enter' && handleSimulate()}
                          />
                          <button 
                              onClick={handleSimulate}
                              disabled={isSimulating || !scenarioInput.trim()}
                              className="w-full bg-purple-600 text-white py-4 rounded-2xl font-black text-lg hover:bg-purple-700 disabled:opacity-50 transition-all shadow-xl shadow-purple-500/20 flex items-center justify-center gap-3 group"
                          >
                              {isSimulating ? <Loader2 className="animate-spin w-6 h-6" /> : <Brain className="w-6 h-6 group-hover:scale-110 transition-transform" />}
                              {t.prediction_scenario_btn}
                          </button>
                      </div>
                  </div>

                  {simulationResult && (
                      <div className="space-y-6 animate-slide-in">
                          <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
                              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-indigo-500"></div>
                              <h3 className="text-2xl font-black mb-4 flex items-center gap-2">
                                  <Sparkles className="w-6 h-6 text-amber-400" />
                                  شیکاری زیرەکانەی سیناریۆ
                              </h3>
                              <p className="text-slate-300 leading-loose text-lg">{simulationResult.summary}</p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {simulationResult.options.map((opt, idx) => (
                                  <div key={idx} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col h-full hover:shadow-md transition-shadow">
                                      <div className="flex justify-between items-start mb-6">
                                          <h4 className="font-black text-slate-800 text-xl">{opt.label}</h4>
                                          <div className={`px-4 py-2 rounded-xl font-black text-sm ${opt.score > 70 ? 'bg-emerald-100 text-emerald-700' : opt.score > 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                              {opt.score}% سەرکەوتن
                                          </div>
                                      </div>
                                      
                                      <div className="space-y-4 flex-1">
                                          <div className="bg-emerald-50/50 p-4 rounded-2xl border border-emerald-100/50">
                                              <span className="text-xs font-black text-emerald-600 uppercase mb-2 block">لایەنە ئەرێنییەکان</span>
                                              <ul className="text-sm text-slate-600 space-y-1">
                                                  {opt.pros.map((p, i) => <li key={i} className="flex items-center gap-2"> <div className="w-1 h-1 bg-emerald-400 rounded-full" /> {p}</li>)}
                                              </ul>
                                          </div>
                                          <div className="bg-red-50/50 p-4 rounded-2xl border border-red-100/50">
                                              <span className="text-xs font-black text-red-600 uppercase mb-2 block">مەترسییەکان</span>
                                              <ul className="text-sm text-slate-600 space-y-1">
                                                  {opt.cons.map((c, i) => <li key={i} className="flex items-center gap-2"> <div className="w-1 h-1 bg-red-400 rounded-full" /> {c}</li>)}
                                              </ul>
                                          </div>
                                      </div>

                                      <div className="mt-6 pt-6 border-t border-slate-50">
                                          <div className="flex items-center gap-2 text-indigo-600 mb-2">
                                              <Clock className="w-4 h-4" />
                                              <span className="text-xs font-black uppercase">{t.prediction_impact_5yr}</span>
                                          </div>
                                          <p className="text-sm text-slate-500 italic">"{opt.impact}"</p>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      </div>
                  )}
              </div>
          )}

          {activeTab === 'alerts' && (
              <div className="space-y-4">
                  {loadingAlerts ? (
                      <div className="flex flex-col items-center justify-center py-20">
                          <Loader2 className="animate-spin w-10 h-10 text-purple-300 mb-4" />
                          <p className="text-slate-400 font-bold">خەریکم لێکدانەوەی ئەگەرەکان دەکەم...</p>
                      </div>
                  ) : (
                      alerts.map((alert) => (
                          <div key={alert.id} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col md:flex-row gap-6 items-center animate-fade-in">
                              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${getAlertColor(alert.type)}`}>
                                  {alert.type === 'health' ? <Activity className="w-8 h-8" /> : alert.type === 'finance' ? <TrendingUp className="w-8 h-8" /> : alert.type === 'work' ? <ShieldCheck className="w-8 h-8" /> : <GitMerge className="w-8 h-8" />}
                              </div>
                              <div className="flex-1 text-center md:text-right">
                                  <div className="flex flex-col md:flex-row md:items-center gap-2 mb-1">
                                      <span className="font-black text-slate-800 text-lg">{alert.message}</span>
                                      <span className="bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-0.5 rounded-full uppercase">{alert.timeframe}</span>
                                  </div>
                                  <p className="text-slate-500 text-sm">{alert.suggestion}</p>
                              </div>
                              <div className="w-full md:w-32 flex flex-col items-center">
                                  <span className="text-xs font-black text-slate-400 mb-2">ئەگەری ڕوودان</span>
                                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                                      <div className={`h-full transition-all duration-1000 ${alert.probability > 70 ? 'bg-red-500' : alert.probability > 40 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${alert.probability}%` }}></div>
                                  </div>
                                  <span className="text-sm font-black text-slate-700 mt-1">{alert.probability}%</span>
                              </div>
                          </div>
                      ))
                  )}
              </div>
          )}
      </div>
    </div>
  );
};

export default PredictionLab;
